import styled from 'styled-components';
import { events } from '../../../hooks/tracking/analytics';
import { Flex, FlexColumn } from '../../Flex';
import {
  ChatMessage as ChatMessageType,
  OnUserInteraction,
  StreamEvent,
  ToolInvocationEvent,
  ToolRendererFunction,
} from '../types';
import FeedbackButtons from '../../FeedbackButtons';
import MessageContextChips from './MessageContextChips';
import TextMessageRenderer from './TextMessageRenderer';
import ToolInvocationRenderer from './ToolInvocationRenderer';
import ToolCallGroup from './ToolCallGroup';
import { getApprovalToolRenderer } from './ApprovalToolRenderer';
import { hasFEToolRenderer } from './FEToolRenderer';

const Wrapper = styled(Flex)<{ $maxWidth?: number }>`
  align-items: flex-start;
  gap: 8px;
  ${({ $maxWidth }) =>
    $maxWidth &&
    `
    max-width: ${$maxWidth}px;
    width: 100%;
    margin: 0 auto;
  `}
`;

const Message = styled(FlexColumn)<{ $isUser: boolean }>`
  background: ${({ theme, $isUser }) =>
    $isUser ? theme.palette.common.white : 'transparent'};
  padding: ${({ $isUser }) => ($isUser ? '10px 12px' : '0')};
  border-radius: 8px;
  border: ${({ $isUser, theme }) =>
    $isUser ? `1px solid ${theme.palette.common.gray5}` : 'none'};
  ${({ $isUser }) =>
    $isUser
      ? 'width: fit-content; margin: 0 0 0 auto;'
      : 'flex: 1; min-width: 0;'}
  gap: 8px;
`;

const UserMessage = styled.span`
  font-size: 14px;
  unicode-bidi: plaintext;
`;

UserMessage.defaultProps = {
  dir: 'auto',
};

type GroupedPart =
  | StreamEvent
  | {
      type: 'tool-group';
      invocations: ToolInvocationEvent['toolInvocation'][];
    };

const groupToolParts = (parts: StreamEvent[]): GroupedPart[] => {
  const result: GroupedPart[] = [];
  let buffer: ToolInvocationEvent['toolInvocation'][] = [];
  const excludedToolCallIds = new Set<string>();

  const flushBuffer = () => {
    const uniqueIds = new Set(buffer.map((inv) => inv.toolCallId));
    if (uniqueIds.size >= 2) {
      result.push({ type: 'tool-group', invocations: [...buffer] });
    } else if (buffer.length >= 1) {
      const part: ToolInvocationEvent = {
        type: 'tool-invocation',
        toolInvocation: buffer[buffer.length - 1],
      };
      result.push(part);
    }
    buffer = [];
  };

  for (const part of parts) {
    if (part.type === 'tool-invocation') {
      const { toolInvocation } = part;
      const isExcluded =
        excludedToolCallIds.has(toolInvocation.toolCallId) ||
        toolInvocation.type === 'approval' ||
        !!getApprovalToolRenderer(toolInvocation.toolName) ||
        hasFEToolRenderer(toolInvocation.toolName);

      if (isExcluded) {
        excludedToolCallIds.add(toolInvocation.toolCallId);
        buffer = buffer.filter(
          (inv) => inv.toolCallId !== toolInvocation.toolCallId,
        );
        flushBuffer();
        result.push(part);
      } else {
        buffer.push(toolInvocation);
      }
    } else if (part.type !== 'step-start') {
      flushBuffer();
      result.push(part);
    }
  }
  flushBuffer();

  return result;
};

type Props = {
  message?: ChatMessageType;
  isDebug?: boolean;
  onApproveToolUse?: (
    val: boolean,
    interruptId?: string,
    newArgs?: Record<string, unknown>,
  ) => void;
  onUserInteraction?: OnUserInteraction;
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  contentMaxWidth?: number;
};

const ChatMessage = ({
  message,
  isDebug = false,
  onApproveToolUse,
  onUserInteraction,
  getToolRenderer,
  contentMaxWidth,
}: Props) => (
  <Wrapper $maxWidth={contentMaxWidth}>
    {message && (
      <Message $isUser={message.role === 'user'}>
        {groupToolParts(message.parts ?? []).map((part, idx) => {
          if (part.type === 'tool-group') {
            return <ToolCallGroup key={idx} invocations={part.invocations} />;
          }

          if (part.type === 'text') {
            return message.role === 'user' ? (
              <UserMessage key={idx}>
                {message.context && (
                  <MessageContextChips context={message.context} />
                )}
                {part.text}
              </UserMessage>
            ) : (
              <TextMessageRenderer key={idx} text={part.text} />
            );
          }

          if (part.type === 'tool-invocation') {
            return (
              <ToolInvocationRenderer
                key={idx}
                invocation={part.toolInvocation}
                isDebug={isDebug}
                onApproveToolUse={onApproveToolUse}
                onUserInteraction={onUserInteraction}
                getToolRenderer={getToolRenderer}
                contentMaxWidth={contentMaxWidth}
              />
            );
          }
          if (part.type === 'conversation-end')
            return (
              <FeedbackButtons
                key={idx}
                message={message.content}
                eventType={events.AI_CHAT_MESSAGE_FEEDBACK}
              />
            );
          return null;
        })}
      </Message>
    )}
  </Wrapper>
);

export default ChatMessage;
